import { useMemo } from 'react';
import { createPersistedQueryLink } from 'apollo-link-persisted-queries';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache, IntrospectionFragmentMatcher, defaultDataIdFromObject } from 'apollo-cache-inmemory';
import { HttpLink } from 'apollo-link-http';

import { isBrowser } from 'utils/misc-utils';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { OUTLETS_RELEASE } from 'shared/constants/api-versions';

import possibleTypes from 'shared/schemas/fragment-types';
import PublicEnv from 'shared/utils/public-env';
import LogRocket from 'shared/logrocket';

import { apolloSessionLink } from 'utils/session';
import { networkStatusLink } from './network-status';
import { cellRoutedFetch, setCellContext } from './cell-routing';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: possibleTypes,
});

let globalApolloClient = null;

let { consumerUrl } = PublicEnv;

if (isBrowser()) {
  consumerUrl = ``;
} else if (!consumerUrl) {
  consumerUrl = `http://localhost:${process.env.PORT || 3000}`;
}

export const EXTENSION_ERRORS = {
  AUTHERROR: 'AUTHENTICATION_ERROR',
  SESSIONEXPIRED: 'SESSION_INVALID',
};

const handleSessionExpired = (error) => {
  if (isBrowser()) {
    console.error(error);
    // "Simply remove token when session expires. Redirects should be handled at the page level."
    setPersistedValue('access-token', false);
  }
};

const errorLinkFactory = ({ onSessionExpired, onAuthError } = {}) =>
  onError(({ graphQLErrors, networkError, _operation }) => {
    // console.error(`Error in the following operation: ${operation}`);

    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        console.error(error);
        if (error.extensions?.code === EXTENSION_ERRORS.AUTHERROR) {
          onAuthError.call(this, error);
        }
        if (error.extensions && error.extensions.code === EXTENSION_ERRORS.SESSIONEXPIRED) {
          // console.error('Session expired');
          if (onSessionExpired) {
            onSessionExpired.call(this, error);
          }
        }
      });
    }

    if (networkError) {
      console.error(`Network Error: ${networkError.message} (${networkError.statusCode})`);
    }
  });

export function setHeaders(operation, forward) {
  // API version indicates which features this client understands.
  // Used for backward-compatible feature rollouts (e.g., outlet collections).
  // Sent for both SSR and client-side requests to ensure consistent behavior.
  // @see shared/constants/api-versions.ts for available version constants.
  const headers = {
    'x-api-version': OUTLETS_RELEASE,
  };

  if (isBrowser()) {
    const token = getPersistedValue('access-token');
    headers.url = window.location.href;

    if (token) {
      headers.authorization = token;
    }

    if (LogRocket) {
      LogRocket.getSessionURL((sessionURL) => {
        headers['X-LogRocket-URL'] = sessionURL;
      });
    }
  }

  operation.setContext(() => ({
    headers: { ...operation.getContext().headers, ...headers },
  }));

  return forward(operation);
}

function dataIdFromObject(object) {
  if (object.__typename === 'Products' && object.adTrackers) {
    return null; // Don't normalize sponsored products
  }
  return defaultDataIdFromObject(object);
}

/**
 * Creates and configures the ApolloClient
 */
export function createApolloClient() {
  const name = `Marketplace (${PublicEnv.appEnv})`;

  const errorLink = errorLinkFactory({ onSessionExpired: handleSessionExpired, onAuthError: handleSessionExpired });

  const httpLink = new HttpLink({
    credentials: 'include',
    fetch: cellRoutedFetch,
    uri: `${consumerUrl}/graphql`,
    useGETForHashedQueries: true,
  });

  return new ApolloClient({
    connectToDevTools: isBrowser(),
    ssrMode: !isBrowser(),
    name,
    version: PublicEnv.currentVersion,
    assumeImmutableResults: true,
    cache: new InMemoryCache({ dataIdFromObject, fragmentMatcher, freezeResults: true }),
    link: ApolloLink.from([
      setHeaders,
      apolloSessionLink,
      errorLink,
      createPersistedQueryLink({ useGETForHashedQueries: true }),
      setCellContext,
      networkStatusLink,
      httpLink,
    ]),
  });
}

export function initializeApollo(initialState = null) {
  const apolloClient = globalApolloClient || createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    // TODO: What does that mean? --^
    const existingCache = apolloClient.extract();
    // Restore the cache using the data passed from getStaticProps/getServerSideProps
    // combined with the existing cached data
    apolloClient.cache.restore({ ...existingCache, ...initialState });
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') {
    return apolloClient;
  }
  // Create the Apollo Client once in the client
  if (!globalApolloClient) {
    globalApolloClient = apolloClient;
  }
  return apolloClient;
}

export function useApollo(initialState) {
  const client = useMemo(() => initializeApollo(initialState), [initialState]);
  return client;
}
